import { storeContainer } from '../Store'
import { countriesJson } from 'src/static/geography'
import { convertToBoolean } from 'src/utils/commonUtils'
import { i18nTranslate } from 'src/utils/i18n'
import { observable, action } from 'mobx'

/**
 * NLXXConfigContainer class.
 * Handles NLXX (Netherlands cross-border) shipping configuration.
 * Provides cached access to NLXX settings from store properties.
 */
class NLXXConfigContainer {
  // Observable configuration properties
  @observable enableNLXX = false
  @observable nlxxAllowedCodes = []
  @observable nlxxCountryOptions = []
  @observable nlxxConfigStoreId = undefined

  /**
   * Load and cache NLXX configuration. Should be called on componentDidMount.
   * Parses the NLXXConfig property once per store and stores in observable properties.
   * Cache is automatically invalidated when activeStoreId changes.
   * @returns {{ enableNLXX: boolean, nlxxAllowedCodes: string[], nlxxCountryOptions: Array }}
   */
  @action
  loadNLXXConfig() {
    const currentStoreId = storeContainer.activeStoreId

    // Return cached config if store hasn't changed
    if (this.enableNLXX && this.nlxxConfigStoreId === currentStoreId) {
      return {
        enableNLXX: this.enableNLXX,
        nlxxAllowedCodes: this.nlxxAllowedCodes,
        nlxxCountryOptions: this.nlxxCountryOptions,
      }
    }

    // Parse and compute config (only when cache is invalid)
    const crossMarketTaxConfig = storeContainer.getCrossMarketTaxConfig() || {}

    let enableNLXX = false
    let nlxxAllowedCodes = []
    let nlxxCountryOptions = []

    enableNLXX = convertToBoolean(crossMarketTaxConfig?.enableCrossMarketTax)
    if (enableNLXX && crossMarketTaxConfig?.allowedCountries) {
      nlxxAllowedCodes = crossMarketTaxConfig.allowedCountries
        .split(',')
        .map(country => country.trim().toUpperCase())
        .filter(Boolean)
      nlxxCountryOptions = countriesJson().filter(
        country => country.value && nlxxAllowedCodes.includes(country.value)
      )
    }

    // Store in observable properties
    this.enableNLXX = enableNLXX
    this.nlxxAllowedCodes = nlxxAllowedCodes
    this.nlxxCountryOptions = nlxxCountryOptions
    this.nlxxConfigStoreId = storeContainer.activeStoreId

    return { enableNLXX, nlxxAllowedCodes, nlxxCountryOptions }
  }

  /**
   * Validates if a country code is allowed for NLXX shipping
   * @param {string} countryCode - Country code to validate
   * @returns {{ isAllowedCountries: boolean, countryName: string, message: string|null }}
   */
  validateNLXXDestination = countryCode => {
    if (!this.enableNLXX) {
      return { isAllowedCountries: true, countryName: '', message: null }
    }
    const normalizedCode = countryCode?.toUpperCase() || ''
    const isAllowedCountries = this.nlxxAllowedCodes.includes(normalizedCode)
    const countryOption = this.nlxxCountryOptions.find(
      c => c.value === normalizedCode
    )
    const countryName = countryOption?.label || normalizedCode

    return {
      isAllowedCountries,
      countryName,
      message: isAllowedCountries
        ? null
        : i18nTranslate(
            'nlxx.invalidDestination',
            'Shipping to {countryName} is not available.',
            { countryName }
          ),
    }
  }

  /**
   * Check if a country code is allowed for NLXX shipping
   * @param {string} countryCode - Country code to check
   * @returns {boolean}
   */
  isCountryAllowed(countryCode) {
    if (!this.enableNLXX || !countryCode) {
      return false
    }
    const normalizedCode = countryCode.toUpperCase()
    return this.nlxxAllowedCodes.includes(normalizedCode)
  }

  /**
   * Get formatted country codes as a pipe-separated string
   * Used for API query parameters
   * @returns {string}
   */
  getCountryCodesAsString() {
    return this.nlxxAllowedCodes.join('|')
  }

  /**
   * Clear the cache manually (useful for testing or forced refresh)
   */
  @action
  clearCache() {
    this.enableNLXX = false
    this.nlxxAllowedCodes = []
    this.nlxxCountryOptions = []
    this.nlxxConfigStoreId = undefined
  }
}

const nlxxConfigContainer = new NLXXConfigContainer()

export { nlxxConfigContainer, NLXXConfigContainer }
export default nlxxConfigContainer
